import { ChangeEvent } from 'react';
import { IconInfoCircleOutline, IconPound } from '@iwoca/icons';
import { AlertBox, Card, Input, Select } from '@iwoca/orion';
import { Trans } from '@lingui/react/macro';
import { PURPOSEOPTIONS } from 'components/addNewApplication/PurposeOfFundsHelper';
import { InformationMessage } from 'components/InformationMessage/InformationMessage';
import { useIntroducerData } from 'contexts/IntroducerData/IntroducerData.context';
import { useAmplitude } from 'hooks/useAmplitude';
import { TDuration } from 'lendingApi/applications.types';
import { Controller, useFormContext } from 'react-hook-form';
import {
  REFINANCING_AMOUNT_THRESHOLD,
  REQUESTED_AMOUNT_CONTACT_THRESHOLD,
} from 'utils/applications/signup.config';
import { convertCurrencyToNumber } from 'utils/currencyConverter';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { useSignupData } from '../hooks/useSignupData';
import {
  getTotalDebt,
  hasEnteredDebtData,
  overRequestedAmountMessage,
} from '../RefinancingDebts/refinancing.helpers';
import {
  useRefinancingEnabled,
  useShowRefinancing,
} from '../RefinancingDebts/useShowRefinancing.hook';
import signupFormStyles from '../SignupForm/SignupForm.module.css';
import { TSignupFormData } from '../SignupForm/SignupForm.types';

import styles from './LoanApplication.module.css';

export const getLoanDuration = (
  productType?: string | null,
): TDuration | null => {
  switch (productType) {
    case 'recovery_loan':
      return '60-months';
    case 'flexi24':
      return '24-months';
    case 'credit_facility':
      return '12-months';
    default:
      return null;
  }
};

export const LoanApplication = () => {
  const { availableProducts } = useIntroducerData();
  const { trackEvent } = useAmplitude();
  const {
    register,
    control,
    watch,
    formState: { errors },
  } = useFormContext<TSignupFormData>();
  const { isReintroduction } = useSignupData();
  const { checkIntroducerPermission } = useIntroducerData();
  const isDisabled = !checkIntroducerPermission('can_create_applications');

  const handleProductTypeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    if (!e.target.value) return;

    trackEvent(AMPLITUDE_EVENTS.productTypeSelected, {
      'Loan Duration': e.target.value,
    });
  };

  const loanPurposeField = register('loanPurpose');
  const handleLoanPurposeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    // Our onChange overrides register's, so call it manually or the refinancing
    // gate (which watches loanPurpose) won't update.
    loanPurposeField.onChange(e);
    trackEvent(AMPLITUDE_EVENTS.loanPurposeSelected, {
      'Loan purpose': e.target.value,
    });
  };

  const aboveContactThreshold =
    Number(watch('amount')?.replace(/[^0-9]+/g, '')) >=
    REQUESTED_AMOUNT_CONTACT_THRESHOLD;

  // Warn that entered debt details are hidden (but preserved) when the amount
  // drops below the threshold. Amount-specific, so not shown on a purpose change.
  const refinancingEnabled = useRefinancingEnabled();
  const isRefinancingPurpose = watch('loanPurpose') === 'refinancing_debt';
  const amountBelowThreshold =
    Number(watch('amount')?.replace(/[^0-9]+/g, '')) <
    REFINANCING_AMOUNT_THRESHOLD;
  const showDebtsHiddenWarning =
    refinancingEnabled &&
    isRefinancingPurpose &&
    amountBelowThreshold &&
    hasEnteredDebtData(watch('refinancing.debts'));

  const showRefinancing = useShowRefinancing({
    amount: watch('amount'),
    loanPurpose: watch('loanPurpose'),
  });

  // Flag over-requesting live, not just on submit.
  const requestedAmount = convertCurrencyToNumber(watch('amount')) || 0;
  const totalDebt = getTotalDebt(watch('refinancing.debts'));
  const overRequestedError =
    showRefinancing && requestedAmount && totalDebt > requestedAmount
      ? overRequestedAmountMessage(totalDebt)
      : undefined;

  return (
    <Card className={signupFormStyles.formSection}>
      <h2>Loan request details</h2>
      <div className={signupFormStyles.cardContents}>
        <div className={signupFormStyles.formField}>
          <Controller
            name="amount"
            control={control}
            render={({ field: { onChange, value } }) => {
              const numericValue = value?.replace(/[^0-9]/g, '') ?? '';
              const formattedValue = numericValue
                ? parseInt(numericValue).toLocaleString()
                : '';

              return (
                <Input
                  name="amount"
                  className={styles.loanAmount}
                  label="Amount"
                  description={
                    isReintroduction
                      ? 'Tell us how much more your client would like to borrow.'
                      : undefined
                  }
                  iconLeft={<IconPound />}
                  type="text"
                  disabled={isDisabled}
                  value={formattedValue}
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/[^0-9]/g, '');
                    onChange(rawValue);
                  }}
                  error={errors.amount?.message || overRequestedError}
                />
              );
            }}
          />
          {aboveContactThreshold &&
            !errors?.amount?.message &&
            !overRequestedError && (
              <InformationMessage message="For amounts of £350,000 and above, our underwriting team may contact the applicant directly. We’ll notify you in advance." />
            )}
          {showDebtsHiddenWarning && (
            <AlertBox className={styles.debtsHiddenAlert} variant="connect">
              <IconInfoCircleOutline
                className={styles.debtsHiddenAlertIcon}
                fontSize={20}
              />
              <p className={styles.debtsHiddenAlertText}>
                Debt details are hidden while the amount is below £
                {REFINANCING_AMOUNT_THRESHOLD.toLocaleString()}. Increase the
                amount to show them again.
              </p>
            </AlertBox>
          )}
        </div>
        <div className={signupFormStyles.formField}>
          <Select
            {...register('loanDuration')}
            onChange={handleProductTypeChange}
            label="Preferred term"
            defaultValue=""
            error={errors.loanDuration && errors.loanDuration.message}
            disabled={isDisabled}
          >
            <option value="" disabled>
              <Trans>Choose an option</Trans>
            </option>
            {availableProducts?.includes('credit_facility') && (
              <option value="12-months">12 months</option>
            )}
            {availableProducts?.includes('flexi24') && (
              <option value="24-months">24 months</option>
            )}
            {availableProducts?.includes('credit_facility') && (
              <>
                <option value="36-months">36 months</option>
                <option value="48-months">48 months</option>
                <option value="60-months">60 months</option>
              </>
            )}
          </Select>
        </div>
        <div className={signupFormStyles.formField}>
          <Select
            {...loanPurposeField}
            label="Loan purpose"
            onChange={handleLoanPurposeChange}
            error={errors.loanPurpose && errors.loanPurpose.message}
            defaultValue=""
            disabled={isDisabled}
          >
            <option value="" disabled>
              <Trans>Choose an option</Trans>
            </option>
            {PURPOSEOPTIONS()
              .filter((option) => option.enabledForUK)
              .map((option) => {
                return (
                  <option value={option.value} key={option.value}>
                    {option.label}
                  </option>
                );
              })}
          </Select>
          {showRefinancing && (
            <p className={styles.refinancingGuidance}>
              When refinancing £{REFINANCING_AMOUNT_THRESHOLD.toLocaleString()}{' '}
              or more, you’ll list each debt your client wants to clear. Your
              client will sign a legal agreement confirming these details. If
              iwoca makes a refinancing offer, it’ll only apply to the debts
              listed here, and the list can’t be changed later.
            </p>
          )}
        </div>
      </div>
    </Card>
  );
};
